/**
 * El resultado de una acción, comunicado de una sola manera.
 *
 * `UI-11`: toda acción da señal de haber ocurrido. Y la da igual en todas
 * partes — con el mensaje del backend cuando lo hay, porque `COD-8` garantiza
 * que está escrito para que lo lea un analista y reescribirlo aquí solo puede
 * empeorarlo.
 *
 * El encabezado y el detalle viajan **separados**: el primero en negrita, el
 * segundo debajo y atenuado. Antes se concatenaban en una frase porque la API
 * del design system solo aceptaba un string; ver `componentes/Avisos.tsx` para
 * por qué se dejó de usar.
 *
 * La política de qué va aquí y qué se queda en la página está en el sistema de
 * diseño de Recuperos §10, y se resume así: **un error que deja una región sin
 * datos no se esconde en un aviso.** Para eso está el `Callout` de `Datos.tsx`.
 */
import { toast } from 'sonner'
import { DURACION } from '../componentes/Avisos.js'
import { ErrorApi } from '../api/cliente.js'

export function avisarExito(mensaje: string, detalle?: string): void {
  toast.success(mensaje, {
    duration: DURACION.exito,
    ...(detalle !== undefined && detalle !== '' ? { description: detalle } : {}),
  })
}

export function avisarInfo(mensaje: string, detalle?: string): void {
  toast.info(mensaje, {
    duration: DURACION.info,
    ...(detalle !== undefined && detalle !== '' ? { description: detalle } : {}),
  })
}

/**
 * Traduce un error a un aviso.
 *
 * El 401 no se avisa: la app ya está redirigiendo a la pantalla de acceso, y
 * un toast de «inicie sesión» sobre el formulario de inicio de sesión es ruido.
 */
export function avisarError(error: unknown, encabezado = 'No se pudo completar la acción'): void {
  if (error instanceof ErrorApi) {
    if (error.esSesionCaducada) return
    toast.error(encabezado, { description: error.message, duration: DURACION.error })
    return
  }
  if (error instanceof Error && error.message !== '') {
    toast.error(encabezado, { description: error.message, duration: DURACION.error })
    return
  }
  toast.error(encabezado, {
    description: 'No fue posible comunicarse con el servidor. Vuelva a intentarlo.',
    duration: DURACION.error,
  })
}

/**
 * Aviso con una acción. **No se auto-descarta**: si se le ofrece al usuario un
 * siguiente paso, no se le puede quitar mientras lo lee.
 */
export function avisarConAccion(
  mensaje: string,
  detalle: string,
  accion: { etiqueta: string; alPulsar: () => void },
): void {
  toast.success(mensaje, {
    description: detalle,
    duration: Infinity,
    action: { label: accion.etiqueta, onClick: accion.alPulsar },
  })
}

/**
 * `«2 consultas»` · `«1 consulta»`.
 *
 * Existe porque los textos de este sistema cuentan cosas constantemente —
 * «Emitir 2 consultas», «afecta a 3 cuentas»— y concordar el plural a mano en
 * cada sitio termina produciendo «1 consultas».
 *
 * El cero se pluraliza («0 consultas»), que es lo correcto en español. El
 * «ninguna» lo pone el llamante si su frase lo pide, porque depende del género
 * del sustantivo y esta función no lo conoce.
 */
export function plural(cantidad: number, singular: string, pluralizado: string): string {
  return `${String(cantidad)} ${cantidad === 1 ? singular : pluralizado}`
}
